import { Injectable } from '@angular/core';
import { isNullish } from '@commonHelpers/math-utils';
import { RequestService } from '@commonServices/request.service';
import { Client as AnalyticsApi, CrossCatalogListingSearchFilterTagsDto, CrossCatalogListingSearchTermsDto, CrossCatalogListingSearchesAvgDto, CrossCatalogListingSearchesBenchmarkDto, CrossCatalogListingSearchesDto, CrossCatalogListingSearchesPerDateDto, CrossCatalogListingSearchesPerMonthDto, CrossCatalogListingSearchesPerWeekDto, DatePickerRange } from '@interfaces/HttpClient/AnalyticsApiModels';
import { firstValueFrom } from 'rxjs';
import { DaterangeService } from './daterange.service';

@Injectable()
export class CatalogDashboardCrossCatalogListingSearchService {


	constructor(
		private analyticsApi: AnalyticsApi,
		private requestService: RequestService,
		private daterangeService: DaterangeService) { }

	async getCrossCatalogListingSearchesCountOfCatalogAsync(catalogId: number, dateRange?: DatePickerRange): Promise<CrossCatalogListingSearchesDto[]> {
		try {
			if (isNullish(dateRange)) {
				return firstValueFrom(this.analyticsApi.catalogAdminCrossCatalogListingSearch_GetCrossCatalogListingSearchesCountOfCatalog(catalogId, (await this.daterangeService.rangeInfo)?.rangeStart, (await this.daterangeService.rangeInfo)?.rangeEnd));
			} else {
				const range = await this.daterangeService.getDatepickerSelectionByRange(dateRange);
				return firstValueFrom(this.analyticsApi.catalogAdminCrossCatalogListingSearch_GetCrossCatalogListingSearchesCountOfCatalog(catalogId, range.rangeStart, range.rangeEnd));
			}

		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async getCrossCatalogListingSearchesCountBenchmarkAsync(catalogId: number, dateRange: DatePickerRange): Promise<CrossCatalogListingSearchesBenchmarkDto[]> {
		try {
			const range = await this.daterangeService.getDatepickerSelectionByRange(dateRange);
			return firstValueFrom(this.analyticsApi.catalogAdminSearchBenchmark_GetCrossCatalogListingSearchesCountBenchmark(catalogId, range.rangeStart, range.rangeEnd));
		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async getCrossCatalogListingSearchesCountCategoryBenchmarkAsync(catalogId: number, dateRange: DatePickerRange): Promise<CrossCatalogListingSearchesBenchmarkDto[]> {
		try {
			const range = await this.daterangeService.getDatepickerSelectionByRange(dateRange);
			return firstValueFrom(this.analyticsApi.catalogAdminSearchBenchmark_GetCrossCatalogListingSearchesCountCategoryBenchmark(catalogId, range.rangeStart, range.rangeEnd));
		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async getCrossCatalogListingSearchesCountCategoryGroupBenchmarkAsync(catalogId: number, dateRange: DatePickerRange): Promise<CrossCatalogListingSearchesBenchmarkDto[]> {
		try {
			const range = await this.daterangeService.getDatepickerSelectionByRange(dateRange);
			return firstValueFrom(this.analyticsApi.catalogAdminSearchBenchmark_GetCrossCatalogListingSearchesCountCategoryGroupBenchmark(catalogId, range.rangeStart, range.rangeEnd));
		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async getCrossCatalogListingSearchFilterTagsCountOfCatalogAsync(catalogId: number): Promise<CrossCatalogListingSearchFilterTagsDto[]> {
		try {
			return firstValueFrom(this.analyticsApi.catalogAdminCrossCatalogListingSearch_GetCrossCatalogListingSearchFilterTagsCountOfCatalog(catalogId, (await this.daterangeService.rangeInfo)?.rangeStart, (await this.daterangeService.rangeInfo)?.rangeEnd));
		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async getCrossCatalogListingSearchTermsCountOfCatalogAsync(catalogId: number): Promise<CrossCatalogListingSearchTermsDto[]> {
		try {
			return firstValueFrom(this.analyticsApi.catalogAdminCrossCatalogListingSearch_GetCrossCatalogListingSearchTermsCountOfCatalog(catalogId, (await this.daterangeService.rangeInfo)?.rangeStart, (await this.daterangeService.rangeInfo)?.rangeEnd));
		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async getCrossCatalogListingSearchesCountOfCatalogAVGAsync(catalogId: number): Promise<CrossCatalogListingSearchesAvgDto> {
		try {
			return firstValueFrom(this.analyticsApi.catalogAdminCrossCatalogListingSearch_GetCrossCatalogListingSearchesCountOfCatalogAVG(catalogId, (await this.daterangeService.rangeInfo)?.rangeStart, (await this.daterangeService.rangeInfo)?.rangeEnd));
		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async getCrossCatalogListingSearchesCountOfCatalogPerDateAsync(catalogId: number): Promise<CrossCatalogListingSearchesPerDateDto[]> {
		try {
			return firstValueFrom(this.analyticsApi.catalogAdminCrossCatalogListingSearch_GetCrossCatalogListingSearchesCountOfCatalogPerDate(catalogId, (await this.daterangeService.rangeInfo)?.rangeStart, (await this.daterangeService.rangeInfo)?.rangeEnd));
		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async getCrossCatalogListingSearchesCountOfCatalogPerMonthAsync(catalogId: number): Promise<CrossCatalogListingSearchesPerMonthDto[]> {
		try {
			return firstValueFrom(this.analyticsApi.catalogAdminCrossCatalogListingSearch_GetCrossCatalogListingSearchesCountOfCatalogPerMonth(catalogId, (await this.daterangeService.rangeInfo)?.rangeStart, (await this.daterangeService.rangeInfo)?.rangeEnd));
		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async getCrossCatalogListingSearchesCountOfCatalogPerWeekAsync(catalogId: number): Promise<CrossCatalogListingSearchesPerWeekDto[]> {
		try {
			return firstValueFrom(this.analyticsApi.catalogAdminCrossCatalogListingSearch_GetCrossCatalogListingSearchesCountOfCatalogPerWeek(catalogId, (await this.daterangeService.rangeInfo)?.rangeStart, (await this.daterangeService.rangeInfo)?.rangeEnd));
		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

}
